import React, { useEffect, useState } from 'react'
import { css } from '@styled-system/css'
import {
  TabContainer,
  TabPane,
  TabContent,
  Nav,
  NavItem
} from 'react-bootstrap'
import styledComponents from 'styled-components'
import { Box, Container, Fade, Flex, P } from 'ui/acro'
import { H1 } from 'ui/bend'
import ClassResultsPage from './ResultsPages/ClassResultsPage'
import InstructorResultsPage from './ResultsPages/InstructorResultsPage'
import PlaylistResultsPage from './ResultsPages/PlaylistResultsPage'
import SeriesResultsPage from './ResultsPages/SeriesResultsPage'
import TopResultsPage from './ResultsPages/TopResultsPage'
import { useCommonEntryContext } from 'components_v2/common'
import { useNavigate } from 'react-router-dom'

const StickyNavAndTabs = styledComponents(Box)`
  ${css({
    backgroundColor: 'white',
    boxShadow: `0px 4px 22px rgba(231, 231, 231, 0.25)`,
    padding: ['15px 0', '23px 0'],
    position: [null, null, null, 'sticky'],
    top: [null, null, null, '80px'],
    zIndex: [null, null, null, '2']
  })}
`
const ResultsHeader = styledComponents(Flex)`
${css({
  display: 'flex',
  gap: ['5px', '8px'],
  alignItems: 'baseline',
  pb: ['6px', '10px', '10px', '14px']
})}
`
const ResultsText = styledComponents(P)`
${css({
  fontWeight: ['600'],
  fontSize: ['18px'],
  lineHeight: ['24px'],
  letterSpacing: ['0.396px']
})}
`
const ResultsTextLarge = styledComponents(H1)((props) =>
  css({
    fontWeight: ['600'],
    fontSize: ['38px', '42px', '42px', '48px'],
    fontFamily: props.theme.fonts.Proxima,
    lineHeight: ['48px', '50px', '50px', '56px'],
    letterSpacing: ['-1.14px', '-1.26px', '-1.26px', '-1.44px']
  })
)
const SearchNavTabs = styledComponents(Nav)`
  ${css({
    display: 'flex',
    gap: '16px',
    overflow: 'auto',
    width: '100%',
    '.active > a': {
      color: 'black',
      borderBottom: '1px solid black'
    },
    '.hidden': {
      display: 'none'
    }
  })}
`
const TabTitle = styledComponents(NavItem)((props) =>
  css({
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '24px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    padding: '10px 0',
    a: {
      fontFamily: props.theme.fonts.Proxima,
      color: '#636363',
      pb: '5px'
    }
  })
)

const SearchNavAndTabs = ({ data }) => {
  const { searchParams } = useCommonEntryContext()
  const tab = searchParams?.get('tab') || ''
  const query = searchParams?.get('q') || ''
  const [activeKey, setActiveKey] = useState('top')
  const navigate = useNavigate()
  useEffect(() => {
    setActiveKey(tab || 'top')
  }, [tab])

  const handleTabChange = (t) => navigate(`/search?q=${query}&tab=${t}`)

  const categoryTitles = [
    {
      title: 'top',
      resultCount: data.totalResultsReturned
    },
    {
      title: 'instructors',
      resultCount: data.instructors.resultCount
    },
    {
      title: 'classes',
      resultCount: data.classes.resultCount
    },
    {
      title: 'programs',
      resultCount: data.series.resultCount
    },
    {
      title: 'playlists',
      resultCount: data.playlists.resultCount
    }
  ]

  return (
    <Fade>
      <TabContainer
        id='Search'
        activeKey={activeKey}
        onSelect={handleTabChange}
        generateChildId={(eventKey, type) => `Search-${type}-${eventKey}`}
        transition='false'
      >
        <>
          <StickyNavAndTabs>
            <Container>
              <ResultsHeader>
                <ResultsText className='search-nav'>
                  Search Results for{' '}
                </ResultsText>
                <ResultsTextLarge>{query}</ResultsTextLarge>
              </ResultsHeader>
              <SearchNavTabs>
                {categoryTitles.map((tab) => {
                  return (
                    <TabTitle
                      key={tab.title}
                      title={tab.title}
                      role='tab'
                      eventKey={tab.title}
                      className={tab.resultCount < 1 ? 'hidden' : ''}
                    >
                      {tab.title}
                    </TabTitle>
                  )
                })}
              </SearchNavTabs>
            </Container>
          </StickyNavAndTabs>
          <TabContent>
            {categoryTitles.map((tabPane) => {
              const categoriesForMapping = {
                top: <TopResultsPage query={query} />,
                classes: <ClassResultsPage query={query} />,
                programs: <SeriesResultsPage query={query} />,
                instructors: <InstructorResultsPage query={query} />,
                playlists: <PlaylistResultsPage query={query} />
                // members: <MemberResultsPage query={query} />
              }
              const children = categoriesForMapping[tabPane.title]
                ? categoriesForMapping[tabPane.title]
                : () => null
              return (
                <TabPane
                  key={tabPane.title}
                  eventKey={tabPane.title}
                  unmountOnExit
                  transition='true'
                >
                  <Container minHeight='60vh'>{children}</Container>
                </TabPane>
              )
            })}
          </TabContent>
        </>
      </TabContainer>
    </Fade>
  )
}

export default SearchNavAndTabs
